import {
  BarControllerDatasetOptions,
  CartesianScaleTypeRegistry,
  ChartType,
  CoreInteractionOptions,
  FillerControllerDatasetOptions,
  LegendOptions,
  LineControllerDatasetOptions,
  ScaleOptionsByType,
  ScriptableContext,
  ScriptableScaleContext,
  TooltipOptions
} from 'chart.js'
import { _DeepPartialObject } from 'chart.js/dist/types/utils'
import { color as chartColor } from 'chart.js/helpers'

import { getTheme, hexToRgbString } from 'themes'

import { formatAxisValue } from 'components_new/organisms/Widget/Types/utils'

import { TChartDataset, TChartVariant } from '../types'
import { externalTooltipHandler } from './tooltip'
import { isArray } from 'lodash'

/*-- helpers --*/
const getLastNonNullIndex = (data: (number | null)[]) => {
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i] !== null) return i
  }

  return -1
}

const isLastTick = (ctx: ScriptableScaleContext) => {
  if (!ctx.chart.data.labels) return false

  // assumes chart only has one dataset
  const dataset = ctx.chart.data.datasets?.[0]

  if (!dataset) return false

  const data = dataset.data as (number | null)[]

  const lastIndex = getLastNonNullIndex(data)

  return ctx.index === lastIndex
}

const isLast = (ctx: ScriptableContext<'line'>) => {
  const data = ctx.dataset.data as (number | null)[]

  return ctx.dataIndex === getLastNonNullIndex(data)
}

const getColor = (
  ctx: ScriptableContext<ChartType>,
  dataset: TChartDataset
) => {
  const theme = getTheme('light')
  const defaultColor = theme.palette.primary.main

  const color = isArray(dataset.color)
    ? dataset.color[
      dataset.type === 'bubble' ? ctx.datasetIndex : ctx.dataIndex
    ]
    : dataset.color

  if (color) return color

  return defaultColor ?? '#1a1a1d'
}

/*-- options --*/
export const getChartBackgroundColorOptions = (params: {
  ctx: ScriptableContext<ChartType>
  dataset: TChartDataset
}): string | CanvasGradient => {
  const { ctx, dataset } = params

  const color = getColor(ctx, dataset)

  // bar
  if (dataset.type === 'bar') {
    const i = (ctx.chart as any).$clickableIndex
    const di = (ctx.chart as any).$clickableDatasetIndex

    const indexMatch = i !== null && ctx.dataIndex === i
    const datasetMatch = di === null || ctx.datasetIndex === di

    const isFaded = dataset.style === 'dashed' || dataset.style === 'faded'

    // hover
    if (indexMatch && datasetMatch && color) {
      if (isFaded) return chartColor(color).alpha(0.3).rgbString()

      return chartColor(color).darken(0.1).rgbString()
    }

    if (color) {
      if (isFaded) return chartColor(color).alpha(0.25).rgbString()

      return color
    }

    return color
  }

  // line
  if (dataset.type === 'line') {
    const { chart } = ctx
    const { ctx: c, chartArea } = chart

    const gradientStartColor = hexToRgbString(color, 0.12)
    const gradientEndColor = hexToRgbString(color, 0)

    if (!chartArea) return gradientStartColor // first render

    const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)

    g.addColorStop(0, gradientStartColor)
    g.addColorStop(1, gradientEndColor)

    return g
  }

  // bubble
  if (dataset.type === 'bubble') {
    const isFaded = dataset.style === 'dashed' || dataset.style === 'faded'

    if (color) {
      if (isFaded) return chartColor(color).alpha(0.25).rgbString()

      return color
    }

    return color
  }

  return color
}

export const getChartBorderColorOptions = (params: {
  ctx: ScriptableContext<ChartType>
  dataset: TChartDataset
}): string | CanvasGradient => {
  const { ctx, dataset } = params

  const color = getColor(ctx, dataset)

  return color
}

export const getChartBorderDashOptions = (params: {
  dataset: TChartDataset
}) => {
  const { dataset } = params

  return dataset.style === 'dashed' ? [2, 4] : [0, 0]
}

export const getChartBorderWidthOptions = (params: {
  ctx: ScriptableContext<ChartType>
  dataset: TChartDataset
}): number => {
  const { dataset } = params

  // bar
  if (dataset.type === 'bar') {
    // const i = (ctx.chart as any).$clickableIndex
    // const di = (ctx.chart as any).$clickableDatasetIndex

    // const indexMatch = i !== null && ctx.dataIndex === i
    // const datasetMatch = di === null || ctx.datasetIndex === di

    // if (!indexMatch || !datasetMatch) return 0

    // return 1.5

    return 0
  }

  // line
  return 1.5
}

export const getChartPointBackgroundColorOptions = (params: {
  ctx: ScriptableContext<ChartType>
  dataset: TChartDataset
  variant: TChartVariant
}): string | CanvasGradient => {
  const { ctx, dataset } = params

  const color = getColor(ctx, dataset)

  return color
}

export const getChartPointColorOptions = () => {
  return '#fff'
}

export const getChartPointRadiusOptions = (params: {
  ctx: ScriptableContext<ChartType>
  dataset: TChartDataset
  variant: TChartVariant
}) => {
  const { ctx, dataset, variant } = params

  if (variant === 'minimal' || variant === 'detailed') return 0

  if (dataset.main && isLast(ctx)) return 3

  return 0
}

// ----- OPTIONS -----
export const getChartDatasetsOptions = ():
  | _DeepPartialObject<{
      bar: BarControllerDatasetOptions
      line: LineControllerDatasetOptions & FillerControllerDatasetOptions
    }>
  | undefined => {
  return {
    line: {
      fill: true,
      pointHitRadius: 8,
      pointHoverRadius: 4,
      pointBorderWidth: 1,
      tension: 0.2
    }
  }
}

export const getChartInteractionOptions = ():
  | _DeepPartialObject<CoreInteractionOptions>
  | undefined => {
  return {
    mode: 'index' as const,
    intersect: false
  }
}

export const getChartLegendOptions = (params: {
  nDatasets: number
  variant: TChartVariant
}): _DeepPartialObject<LegendOptions<ChartType>> | undefined => {
  const { nDatasets, variant } = params

  return {
    display: variant === 'detailed' && nDatasets > 1
  }
}

export const getChartScalesOptions = (params: {
  beginYAtZero?: boolean
  isTimeseries?: boolean
  tickLabels?: (string | null)[]
  type: ChartType
  variant: TChartVariant
}):
  | _DeepPartialObject<{
      [key: string]: ScaleOptionsByType<keyof CartesianScaleTypeRegistry>
    }>
  | undefined => {
  const { beginYAtZero, isTimeseries, tickLabels, type, variant } = params

  const theme = getTheme('light')

  return {
    x: {
      ...(tickLabels
        ? {
            afterBuildTicks: (scale: { ticks: { value: number }[] }) => {
              scale.ticks = scale.ticks.filter((tick) => tickLabels[tick.value])
            }
          }
        : {}),
      border: {
        display: variant === 'detailed'
      },
      grid: {
        display: type === 'line' && variant === 'detailed',
        tickLength: variant === 'standard' ? 6 : undefined
      },
      ticks: {
        // autoSkip: false, // @TODO: Do we want this?
        ...(tickLabels
          ? {
              callback: (value: string | number) => tickLabels[Number(value)]
            }
          : {}),
        color: (ctx: ScriptableScaleContext) => {
          if (isTimeseries && isLastTick(ctx)) return theme.palette.text.primary

          return theme.palette.text.secondary
        },
        display: variant !== 'minimal',
        textStrokeColor: (ctx: ScriptableScaleContext) => {
          if (isTimeseries && isLastTick(ctx)) return theme.palette.text.primary

          return undefined
        },
        textStrokeWidth: (ctx: ScriptableScaleContext) => {
          if (isTimeseries && isLastTick(ctx)) return 0.5

          return 0
        }
      }
    },
    y: {
      beginAtZero: beginYAtZero || variant === 'detailed',
      border: {
        display: variant === 'detailed'
      },
      bounds: variant === 'detailed' ? 'ticks' : 'data',
      grace: 0,
      display: variant === 'detailed',
      ticks: {
        // Chart.js would otherwise group by the browser locale
        callback: (value: string | number) =>
          typeof value === 'number' ? formatAxisValue(value, null) : value
      }
    }
  }
}

export const getChartTooltipOptions = (params: {
  compact?: boolean
  unit: TChartDataset['unit']
  variant: TChartVariant
  decimals?: number
  getRows?: Parameters<typeof externalTooltipHandler>[0]['getRows']
  significantFigures?: number
  style?: Partial<CSSStyleDeclaration>
}): _DeepPartialObject<TooltipOptions<ChartType>> => {
  return {
    enabled: false,
    position: 'cursorOffset' as const,
    external: (ctx) => externalTooltipHandler({ ctx, ...params })
  }
}
